import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import {
  selectMeasureDisplay,
  selectMeasureEngine,
  selectMeasureResult,
  selectMeasureTool,
  selectMeasureUnits,
} from '../../store/selectors/index.js';
import {
  areaUnitChanged,
  lengthUnitChanged,
  toolCleared,
  unitSystemChanged,
  type AreaUnit,
  type LengthUnit,
  type UnitSystem,
} from '../../store/slices/measureSlice.js';
import {
  AREA_UNITS,
  AREA_UNIT_LABELS,
  LENGTH_UNITS,
  LENGTH_UNIT_LABELS,
  formatArea,
  formatLength,
  secondaryAreaUnit,
  secondaryLengthUnit,
} from './units.js';
import VertexList from './VertexList.js';

/**
 * The measurement readout.
 *
 * Floats over the map opposite the tool control, on the pattern the identify
 * panel already sets. On a phone it docks to the bottom instead, so it never
 * covers the part of the map being drawn on.
 *
 * Two readouts are deliberately separate. The visible number follows the live
 * preview so it ticks while the pointer moves; the live region announces only
 * committed results, so a screen reader hears a total once per point rather
 * than ten times a second.
 */
export default function MeasurePanel() {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();

  const tool = useAppSelector(selectMeasureTool);
  const display = useAppSelector(selectMeasureDisplay);
  const committed = useAppSelector(selectMeasureResult);
  const engine = useAppSelector(selectMeasureEngine);
  const { system, lengthUnit, areaUnit } = useAppSelector(selectMeasureUnits);

  if (!tool) return null;

  const measuringArea = tool === 'area';
  const lengthLabel = measuringArea ? 'Perimeter' : 'Total distance';

  const length = display ? formatLength(display.lengthMeters, system, lengthUnit) : null;
  /**
   * Segments follow the unit the total resolved to, rather than each scaling
   * itself. Four sides of a square otherwise read 1.00 mi, 1.00 mi, 5,280 ft,
   * 1.00 mi, because one of them lands a hair under the threshold.
   */
  const segmentUnit = length ? (length.chosen as LengthUnit) : lengthUnit;
  const lengthAlso =
    length && lengthUnit === 'auto' ? secondaryLengthUnit(length.chosen as LengthUnit) : null;

  const area =
    display?.areaSquareMeters != null
      ? formatArea(display.areaSquareMeters, system, areaUnit)
      : null;
  const areaAlso = area && areaUnit === 'auto' ? secondaryAreaUnit(area.chosen as AreaUnit) : null;

  /** Announced only when a point is committed, never on a pointer move. */
  const spoken = (() => {
    if (engine === 'unavailable') return 'Measurement is unavailable.';
    if (!committed) return '';
    if (committed.selfIntersecting) return 'The shape crosses itself, so it has no area.';
    const parts = [
      `${lengthLabel} ${formatLength(committed.lengthMeters, system, lengthUnit).spoken}`,
    ];
    if (committed.areaSquareMeters != null) {
      parts.push(`Area ${formatArea(committed.areaSquareMeters, system, areaUnit).spoken}`);
    }
    return `${parts.join('. ')}.`;
  })();

  return (
    <Paper
      elevation={8}
      component="section"
      aria-label="Measurement"
      sx={{
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        zIndex: 2,
        // On a phone the panel would otherwise cover the area being drawn on,
        // so it docks to the bottom of the map and spans it.
        ...(isCompact
          ? { left: 8, right: 8, bottom: 32, maxHeight: '55%' }
          : { top: 12, right: 12, width: 320, maxWidth: 'calc(100% - 24px)', maxHeight: 'calc(100% - 96px)' }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h2" sx={{ flex: 1, fontSize: '0.875rem' }}>
          {measuringArea ? 'Area' : 'Distance'}
        </Typography>
        <IconButton
          size="small"
          onClick={() => dispatch(toolCleared())}
          aria-label="Close measurement"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {engine === 'loading' && <LinearProgress aria-label="Preparing the geometry engine" />}

      <Box sx={{ overflow: 'auto', minHeight: 0 }}>
        <Box sx={{ px: 1.5, py: 1.5 }}>
          {engine === 'unavailable' ? (
            <Typography variant="body2" color="error">
              Measurement is unavailable - the geometry engine could not be loaded.
            </Typography>
          ) : !display ? (
            <Typography variant="body2" color="text.secondary">
              {measuringArea
                ? 'Click three or more points on the map, or add them by coordinates.'
                : 'Click two or more points on the map, or add them by coordinates.'}
            </Typography>
          ) : (
            <>
              {measuringArea &&
                (display.selfIntersecting ? (
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    This shape crosses itself, so it has no area. Move or remove a point.
                  </Typography>
                ) : (
                  area && (
                    <Readout label="Area" value={area.value} unit={area.unit}>
                      {areaAlso &&
                        display.areaSquareMeters != null &&
                        `${formatArea(display.areaSquareMeters, system, areaAlso).value} ${
                          formatArea(display.areaSquareMeters, system, areaAlso).unit
                        }`}
                    </Readout>
                  )
                ))}

              {length && (
                <Readout label={lengthLabel} value={length.value} unit={length.unit}>
                  {lengthAlso &&
                    `${formatLength(display.lengthMeters, system, lengthAlso).value} ${
                      formatLength(display.lengthMeters, system, lengthAlso).unit
                    }`}
                </Readout>
              )}

              {display.segmentMeters.length > 1 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {display.segmentMeters.length} segments
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ listStyle: 'none', m: 0, p: 0, maxHeight: 96, overflow: 'auto' }}
                  >
                    {display.segmentMeters.map((meters, index) => (
                      <Box
                        component="li"
                        key={index}
                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {index + 1} to {index + 2 > display.segmentMeters.length ? 1 : index + 2}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {formatLength(meters, system, segmentUnit).value}{' '}
                          {formatLength(meters, system, segmentUnit).unit}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* The announcement lags the visible number on purpose; see above. */}
        <Box
          aria-live="polite"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          {spoken}
        </Box>

        <Divider />
        <VertexList />
        <Divider />

        <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={system}
            onChange={(_, v: UnitSystem | null) => v && dispatch(unitSystemChanged(v))}
            aria-label="Unit system"
          >
            <ToggleButton value="us" sx={{ px: 2, py: 0.25, fontSize: '0.75rem' }}>
              US customary
            </ToggleButton>
            <ToggleButton value="metric" sx={{ px: 2, py: 0.25, fontSize: '0.75rem' }}>
              Metric
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            select
            size="small"
            label={measuringArea ? 'Perimeter units' : 'Distance units'}
            value={lengthUnit}
            onChange={(e) => dispatch(lengthUnitChanged(e.target.value as LengthUnit | 'auto'))}
          >
            <MenuItem value="auto">Automatic</MenuItem>
            {LENGTH_UNITS[system].map((u) => (
              <MenuItem key={u} value={u}>
                {LENGTH_UNIT_LABELS[u]}
              </MenuItem>
            ))}
          </TextField>

          {measuringArea && (
            <TextField
              select
              size="small"
              label="Area units"
              value={areaUnit}
              onChange={(e) => dispatch(areaUnitChanged(e.target.value as AreaUnit | 'auto'))}
            >
              <MenuItem value="auto">Automatic</MenuItem>
              {AREA_UNITS[system].map((u) => (
                <MenuItem key={u} value={u}>
                  {AREA_UNIT_LABELS[u]}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

interface ReadoutProps {
  readonly label: string;
  readonly value: string;
  readonly unit: string;
  /** The same measurement in a second unit, when one is worth showing. */
  readonly children?: React.ReactNode;
}

function Readout({ label, value, unit, children }: ReadoutProps) {
  return (
    // Grouped and named, so the number, its unit and the second reading are
    // announced together rather than as three loose strings.
    <Box role="group" aria-label={label} sx={{ mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.35rem', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
        {value}{' '}
        <Typography component="span" sx={{ fontSize: '0.875rem' }} color="text.secondary">
          {unit}
        </Typography>
      </Typography>
      {children && (
        <Typography variant="caption" color="text.secondary">
          {children}
        </Typography>
      )}
    </Box>
  );
}
